import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import api from "../services/api";
import "../styles/teams.css";

export default function Teams() {
  // ==================== STATE ====================
  const [currentUser, setCurrentUser]           = useState(null);
  const [loading, setLoading]                   = useState(true);
  const [districts, setDistricts]               = useState([]);
  const [blocksMap, setBlocksMap]               = useState({});
  const [gpsMap, setGpsMap]                     = useState({});
  const [searchTerm, setSearchTerm]             = useState("");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const [expanded, setExpanded]                 = useState({});
  const [districtLoading, setDistrictLoading]   = useState(false);

  const [districtCoords, setDistrictCoords] = useState([]);
  const [blockCoords, setBlockCoords]       = useState([]);
  const [gpCoords, setGpCoords]             = useState([]);
  const [paymentMap, setPaymentMap]         = useState({});

  // Modal state
  const [showModal, setShowModal]       = useState(false);
  const [memberDetails, setMemberDetails] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [activeTab, setActiveTab]       = useState("basic");
  const [lightboxImage, setLightboxImage] = useState(null);
  const [isEditing, setIsEditing]       = useState(false);
  const [editForm, setEditForm]         = useState({});
  const [editPreviews, setEditPreviews] = useState({
    aadhaar: null, pan: null, passbook: null, photo: null,
  });

  const isAdmin = currentUser?.role === "ADMIN";

  // ==================== FETCH CURRENT USER ====================
  useEffect(() => { fetchCurrentUser(); }, []);

  const fetchCurrentUser = async () => {
    try {
      const res = await api.get("/auth/me");
       console.log("rolerolerolerolerole"+res.data)
      setCurrentUser(res.data);
     
    } catch (err) {
      console.error("Failed to get current user", err);
      setCurrentUser({ role: "ADMIN" });
    }
  };

  // ==================== LOAD DATA ON LOGIN ====================
  useEffect(() => { if (currentUser) loadAllData(); }, [currentUser]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [distRes, blockRes, gpRes, paymentRes, districtRes] = await Promise.all([
        api.get("/dashboard/users/by-role/DISTRICT_COORDINATOR"),
        api.get("/dashboard/users/by-role/BLOCK_COORDINATOR"),
        api.get("/dashboard/users/by-role/GP_COORDINATOR"),
        api.get("/users/with-payment-status"),
        api.get("/dashboard/district-member-counts"),
      ]);

      setDistrictCoords(distRes.data || []);
      setBlockCoords(blockRes.data || []);
      setGpCoords(gpRes.data || []);

      const paymentMapTmp = {};
      (paymentRes.data || []).forEach(u => {
        paymentMapTmp[u.id] = { status: u.paymentStatus, amount: u.paymentAmount };
      });
      setPaymentMap(paymentMapTmp);

      const allDistricts = districtRes.data || [];
      setDistricts(allDistricts);

      const newBlocksMap = {};
      const newGpsMap = {};

      // DISTRICT_COORDINATOR: auto-load their own district
      if (currentUser.role === "DISTRICT_COORDINATOR") {
        const myDistricts = allDistricts.filter(d => d.districtId === currentUser.districtId);
        for (const district of myDistricts) {
          const blocksRes = await api.get(`/blocks?districtId=${district.districtId}`);
          const blocks = blocksRes.data || [];
          newBlocksMap[district.districtId] = blocks;
          for (const block of blocks) {
            const gpsRes = await api.get(`/gps?blockId=${block.id}`);
            newGpsMap[block.id] = gpsRes.data || [];
          }
        }
      }

      // BLOCK_COORDINATOR: find their block and GPs
      if (currentUser.role === "BLOCK_COORDINATOR") {
        for (const district of allDistricts) {
          const blocksRes = await api.get(`/blocks?districtId=${district.districtId}`);
          const blocks = blocksRes.data || [];
          const found = blocks.find(b => b.id === currentUser.blockId);
          if (found) {
            newBlocksMap[district.districtId] = [found];
            const gpsRes = await api.get(`/gps?blockId=${currentUser.blockId}`);
            newGpsMap[currentUser.blockId] = gpsRes.data || [];
            break;
          }
        }
      }

      setBlocksMap(newBlocksMap);
      setGpsMap(newGpsMap);
    } catch (err) {
      console.error("Error loading teams data:", err);
    } finally {
      setLoading(false);
    }
  };

  // ==================== LAZY LOAD ON DROPDOWN SELECT ====================
  // Only fetches blocks+GPs for the selected district, and only once (cached)
  const loadDistrictData = async (districtId) => {
    if (!districtId) return;
    if (blocksMap[districtId]) return; // already cached

    setDistrictLoading(true);
    try {
      const blocksRes = await api.get(`/blocks?districtId=${districtId}`);
      const blocks = blocksRes.data || [];

      const newGpsMap = { ...gpsMap };
      for (const block of blocks) {
        const gpsRes = await api.get(`/gps?blockId=${block.id}`);
        newGpsMap[block.id] = gpsRes.data || [];
      }

      setBlocksMap(prev => ({ ...prev, [districtId]: blocks }));
      setGpsMap(newGpsMap);
    } catch (err) {
      console.error("Failed to load district data:", err);
    } finally {
      setDistrictLoading(false);
    }
  };

  const handleDistrictChange = (e) => {
    const val = e.target.value;
    setSelectedDistrict(val);
    loadDistrictData(val);
  };

  // ==================== HELPERS ====================
  const matchesSearch = (coord) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return coord.name?.toLowerCase().includes(term) || coord.email?.toLowerCase().includes(term);
  };

  const filteredDistrictCoords = districtCoords.filter(matchesSearch);
  const filteredBlockCoords    = blockCoords.filter(matchesSearch);
  const filteredGpCoords       = gpCoords.filter(matchesSearch);

  const visibleDistricts = districts.filter(d =>
    selectedDistrict === "" || String(d.districtId) === String(selectedDistrict)
  );

  const getPaymentBadge = (userId) => {
    const p = paymentMap[userId];
    if (!p) return null;
    if (p.status === "PAID")    return <span className="payment-badge paid">✅ Paid</span>;
    if (p.status === "PENDING") return <span className="payment-badge pending">⏳ Pending</span>;
    return <span className="payment-badge free">🆓 Free</span>;
  };

  const getDistrictName = (id) => {
    const d = districts.find(d => d.districtId === id);
    return d ? d.districtName : `District ${id}`;
  };
  const getBlockName = (districtId, blockId) => {
    const b = (blocksMap[districtId] || []).find(b => b.id === blockId);
    return b ? b.name : `Block ${blockId}`;
  };
  const getGpName = (blockId, gpId) => {
    const g = (gpsMap[blockId] || []).find(g => g.id === gpId);
    return g ? g.name : `GP ${gpId}`;
  };

  const toggleExpand = (key) =>
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  // ==================== MEMBER MODAL ====================
  const openMemberModal = async (member) => {
    setShowModal(true);
    setModalLoading(true);
    setActiveTab("basic");
    setIsEditing(false);
    try {
      const userRes = await api.get(`/users/${member.id}`);
      const userData = userRes.data;
      const payment = paymentMap[member.id];
      if (payment) {
        userData.paymentStatus = payment.status;
        userData.paymentAmount = payment.amount;
      }

      const docsRes = await api.get(`/users/documents/${member.id}`);
      const imageFields = {};
      (docsRes.data || []).forEach(doc => {
        const key = doc.type.toLowerCase();
        if (!doc.data) return;
        let b64;
        if (typeof doc.data === "string") {
          b64 = doc.data.startsWith("data:image")
            ? doc.data
            : `data:${doc.fileType || "image/jpeg"};base64,${doc.data}`;
        } else {
          const bytes = new Uint8Array(doc.data);
          let bin = "";
          for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
          b64 = `data:${doc.fileType || "image/jpeg"};base64,${btoa(bin)}`;
        }
        imageFields[`${key}Base64`] = b64;
      });

      const fullDetails = { ...userData, ...imageFields };
      setMemberDetails(fullDetails);
      setEditForm(fullDetails);
      setEditPreviews({ aadhaar: null, pan: null, passbook: null, photo: null });
    } catch (err) {
      console.error("Error loading member details:", err);
      setMemberDetails(member);
      setEditForm(member);
    }
    setModalLoading(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setMemberDetails(null);
    setIsEditing(false);
    setEditForm({});
    setEditPreviews({ aadhaar: null, pan: null, passbook: null, photo: null });
  };

  const handleEditChange = (e) =>
    setEditForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const handleEditFileChange = (type, file) => {
    if (!file) return;
    setEditPreviews(prev => ({ ...prev, [type]: URL.createObjectURL(file) }));
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () =>
      setEditForm(prev => ({ ...prev, [`${type}Base64`]: reader.result }));
  };

  const saveMember = async () => {
    if (!memberDetails?.id) return;
    if (!isAdmin) {
      alert("❌ You don't have permission to edit members.");
      return;
    }
    setModalLoading(true);
    try {
      const documents = ["aadhaar", "pan", "passbook", "photo"]
        .filter(type => editForm[`${type}Base64`]?.startsWith("data:"))
        .map(type => ({
          type: type.toUpperCase(),
          data: editForm[`${type}Base64`].split(",")[1],
          fileType: editForm[`${type}Base64`].match(/data:([^;]+)/)[1],
        }));

      await api.put(`/dashboard/${memberDetails.id}`, {
        name: editForm.name, email: editForm.email, role: editForm.role,
        phone: editForm.phone, employeeId: editForm.employeeId, level: editForm.level,
        village: editForm.village, postOffice: editForm.postOffice, pin: editForm.pin,
        aadhaar: editForm.aadhaar, pan: editForm.pan,
        bankName: editForm.bankName, accountNumber: editForm.accountNumber,
        ifsc: editForm.ifsc, branch: editForm.branch,
        districtId: editForm.districtId, blockId: editForm.blockId,
        gpId: editForm.gpId, stateId: editForm.stateId,
        documents,
      });

      alert("✅ Member updated successfully!");
      await loadAllData();
      setIsEditing(false);

      const updatedUserRes = await api.get(`/users/${memberDetails.id}`);
      const updatedDocsRes = await api.get(`/users/documents/${memberDetails.id}`);
      const updatedImageFields = {};
      (updatedDocsRes.data || []).forEach(doc => {
        const key = doc.type.toLowerCase();
        if (!doc.data) return;
        let b64 = typeof doc.data === "string" ? doc.data
          : btoa([...new Uint8Array(doc.data)].map(b => String.fromCharCode(b)).join(""));
        updatedImageFields[`${key}Base64`] = `data:${doc.fileType || "image/jpeg"};base64,${b64}`;
      });
      const refreshed = { ...updatedUserRes.data, ...updatedImageFields };
      setMemberDetails(refreshed);
      setEditForm(refreshed);
    } catch (err) {
      alert("❌ Error: " + (err.response?.data?.message || err.message));
    } finally {
      setModalLoading(false);
    }
  };

  // ==================== SHARED UI PIECES ====================

  // Filter bar: district dropdown + search — used by ADMIN & STATE_COORDINATOR
  

  // GP section
  const GpSection = ({ gp, bKey }) => {
    const gCoords = filteredGpCoords.filter(c => c.gpId === gp.id);
    const gKey = `${bKey}-gp-${gp.id}`;
    if (searchTerm && gCoords.length === 0) return null;
    return (
      <div className="team-gp">
        <div className="team-gp-header" onClick={() => toggleExpand(gKey)}>
          <span className="toggle-icon">{expanded[gKey] ? "▼" : "▶"}</span>
          <span className="gp-title">🏘️ {gp.name}</span>
          <span className="member-count">{gCoords.length} GP Coordinator{gCoords.length !== 1 ? "s" : ""}</span>
        </div>
        {expanded[gKey] && (
          <div className="team-members">
            {gCoords.map(coord => (
              <div key={coord.id} className="member-card" onClick={() => openMemberModal(coord)}>
                <div className="member-avatar">👤</div>
                <div className="member-info">
                  <div className="member-name">{coord.name}</div>
                  <div className="member-role">{coord.role?.replace(/_/g, " ") || "GP Coordinator"}</div>
                  <div className="member-email">{coord.email}</div>
                  {getPaymentBadge(coord.id)}
                </div>
              </div>
            ))}
            {gCoords.length === 0 && <div className="no-members">No GP coordinators assigned</div>}
          </div>
        )}
      </div>
    );
  };

  // Block section
  const BlockSection = ({ block, dKey }) => {
    const bCoords = filteredBlockCoords.filter(c => c.blockId === block.id);
    const bKey = `${dKey}-block-${block.id}`;
    const gps = gpsMap[block.id] || [];
    const hasMatch = bCoords.length > 0 || gps.some(gp => filteredGpCoords.some(c => c.gpId === gp.id));
    if (searchTerm && !hasMatch) return null;
    return (
      <div className="team-block">
        <div className="team-block-header" onClick={() => toggleExpand(bKey)}>
          <span className="toggle-icon">{expanded[bKey] ? "▼" : "▶"}</span>
          <span className="block-title">📍 {block.name}</span>
          <span className="member-count">{bCoords.length} Block Coordinator{bCoords.length !== 1 ? "s" : ""}</span>
        </div>
        {expanded[bKey] && (
          <div className="team-gps">
            {bCoords.length > 0 && (
              <div className="coordinator-list">
                <div className="coordinator-label">Block Coordinators:</div>
                {bCoords.map(coord => (
                  <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                    👤 {coord.name} {getPaymentBadge(coord.id)}
                  </div>
                ))}
              </div>
            )}
            {gps.map(gp => <GpSection key={gp.id} gp={gp} bKey={bKey} />)}
          </div>
        )}
      </div>
    );
  };

  // District section
  const DistrictSection = ({ district }) => {
    const { districtId, districtName } = district;
    const dCoords = filteredDistrictCoords.filter(c => c.districtId === districtId);
    const blocks  = blocksMap[districtId] || [];
    const dKey    = `district-${districtId}`;
    const isOpen  = expanded[dKey];

    const hasMatch = dCoords.length > 0 ||
      blocks.some(b =>
        filteredBlockCoords.some(c => c.blockId === b.id) ||
        (gpsMap[b.id] || []).some(gp => filteredGpCoords.some(c => c.gpId === gp.id))
      );
    if (searchTerm && !hasMatch) return null;

    return (
      <div className="team-district">
        <div className="team-district-header" onClick={() => toggleExpand(dKey)}>
          <span className="toggle-icon">{isOpen ? "▼" : "▶"}</span>
          <span className="district-title">🏛️ {districtName}</span>
          <span className="member-count">{dCoords.length} District Coordinator{dCoords.length !== 1 ? "s" : ""}</span>
        </div>
        {isOpen && (
          <div className="team-blocks">
            {/* Show loading spinner while fetching blocks for this district */}
            {districtLoading && blocks.length === 0 ? (
              <div className="loading-spinner" style={{ padding: "20px" }}>Loading blocks...</div>
            ) : (
              <>
                {dCoords.length > 0 && (
                  <div className="coordinator-list">
                    <div className="coordinator-label">District Coordinators:</div>
                    {dCoords.map(coord => (
                      <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                        👤 {coord.name} {getPaymentBadge(coord.id)}
                      </div>
                    ))}
                  </div>
                )}
                {blocks.map(block => <BlockSection key={block.id} block={block} dKey={dKey} />)}
                {blocks.length === 0 && !districtLoading && (
                  <div className="no-data" style={{ padding: "12px" }}>No blocks found for this district.</div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  // Modal
  const renderModal = () => {
    if (!showModal) return null;
    return (
      <div className="modal-overlay" onClick={closeModal}>
        <div className="modal-container member-detail-modal" onClick={e => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-header-info">
              <div className="modal-avatar">👤</div>
              <div>
                <h3>{memberDetails?.name || "Member Details"}</h3>
                <p className="modal-role">{memberDetails?.role || "Coordinator"}</p>
              </div>
            </div>
            <button className="modal-close" onClick={closeModal}>×</button>
          </div>

          <div className="modal-body">
            {modalLoading ? (
              <div className="loading-spinner">Loading...</div>
            ) : memberDetails ? (
              !isEditing ? (
                // ---- VIEW MODE ----
                <div className="member-details-tabs">
                  <div className="tabs-header">
                    {["basic", "address", "bank", "hierarchy", "images"].map(tab => (
                      <button key={tab} className={`tab-btn ${activeTab === tab ? "active" : ""}`}
                        onClick={() => setActiveTab(tab)}>
                        {{ basic: "📋 Basic", address: "📍 Address", bank: "🏦 Bank",
                           hierarchy: "🏢 Hierarchy", images: "🖼️ Images" }[tab]}
                      </button>
                    ))}
                  </div>
                  <div className="tab-content">
                    {activeTab === "basic" && (
                      <div className="detail-grid">
                        <div className="detail-item"><label>Full Name</label><p>{memberDetails.name || "—"}</p></div>
                        <div className="detail-item"><label>Email</label><p>{memberDetails.email || "—"}</p></div>
                        <div className="detail-item"><label>Phone</label><p>{memberDetails.phone || "—"}</p></div>
                        <div className="detail-item"><label>Employee ID</label><p>{memberDetails.employeeId || "—"}</p></div>
                        <div className="detail-item"><label>Access Level</label><p>{memberDetails.level || "—"}</p></div>
                        <div className="detail-item"><label>Role</label><p>{memberDetails.role?.replace(/_/g, " ") || "—"}</p></div>
                        <div className="detail-item"><label>Payment Status</label><p>{getPaymentBadge(memberDetails.id) || "—"}</p></div>
                        <div className="detail-item"><label>Payment Amount</label><p>{memberDetails.paymentAmount ? `₹${memberDetails.paymentAmount}` : "—"}</p></div>
                      </div>
                    )}
                    {activeTab === "address" && (
                      <div className="detail-grid">
                        <div className="detail-item"><label>Village/Town</label><p>{memberDetails.village || "—"}</p></div>
                        <div className="detail-item"><label>Post Office</label><p>{memberDetails.postOffice || "—"}</p></div>
                        <div className="detail-item"><label>PIN Code</label><p>{memberDetails.pin || "—"}</p></div>
                        <div className="detail-item"><label>Aadhaar No.</label><p>{memberDetails.aadhaar || "—"}</p></div>
                        <div className="detail-item"><label>PAN No.</label><p>{memberDetails.pan || "—"}</p></div>
                      </div>
                    )}
                    {activeTab === "bank" && (
                      <div className="detail-grid">
                        <div className="detail-item"><label>Bank Name</label><p>{memberDetails.bankName || "—"}</p></div>
                        <div className="detail-item"><label>Account Number</label><p>{memberDetails.accountNumber || "—"}</p></div>
                        <div className="detail-item"><label>IFSC Code</label><p>{memberDetails.ifsc || "—"}</p></div>
                        <div className="detail-item"><label>Branch</label><p>{memberDetails.branch || "—"}</p></div>
                      </div>
                    )}
                    {activeTab === "hierarchy" && (
                      <div className="detail-grid">
                        <div className="detail-item"><label>District</label><p>{getDistrictName(memberDetails.districtId)}</p></div>
                        <div className="detail-item"><label>Block</label><p>{getBlockName(memberDetails.districtId, memberDetails.blockId)}</p></div>
                        <div className="detail-item"><label>Gram Panchayat</label><p>{getGpName(memberDetails.blockId, memberDetails.gpId)}</p></div>
                      </div>
                    )}
                    {activeTab === "images" && (
                      <div className="images-grid">
                        {["aadhaar", "pan", "passbook", "photo"].map(type =>
                          memberDetails[`${type}Base64`] ? (
                            <div key={type} className="image-card"
                              onClick={() => setLightboxImage(memberDetails[`${type}Base64`])}>
                              <img src={memberDetails[`${type}Base64`]} alt={type} />
                              <span>{type.charAt(0).toUpperCase() + type.slice(1)}</span>
                            </div>
                          ) : (
                            <div key={type} className="image-card empty">No {type}</div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // ---- EDIT MODE (ADMIN ONLY) ----
                <div className="edit-form-container">
                  <h3>Edit Member</h3>
                  <div className="detail-section">
                    <h4>Basic Information</h4>
                    <div className="detail-grid">
                      {[["name","Name"],["email","Email"],["phone","Phone"],["employeeId","Employee ID"],["level","Access Level"]].map(([name, label]) => (
                        <div key={name} className="detail-item">
                          <label>{label}</label>
                          <input name={name} value={editForm[name] || ""} onChange={handleEditChange} />
                        </div>
                      ))}
                      <div className="detail-item">
                        <label>Role</label>
                        <select name="role" value={editForm.role || ""} onChange={handleEditChange}>
                          <option value="">Select Role</option>
                          <option value="STATE_COORDINATOR">State Coordinator</option>
                          <option value="DISTRICT_COORDINATOR">District Coordinator</option>
                          <option value="BLOCK_COORDINATOR">Block Coordinator</option>
                          <option value="GP_COORDINATOR">GP Coordinator</option>
                        </select>
                      </div>
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>Address</h4>
                    <div className="detail-grid">
                      {[["village","Village/Town"],["postOffice","Post Office"],["pin","PIN Code"],["aadhaar","Aadhaar"],["pan","PAN"]].map(([name, label]) => (
                        <div key={name} className="detail-item">
                          <label>{label}</label>
                          <input name={name} value={editForm[name] || ""} onChange={handleEditChange} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>Bank Details</h4>
                    <div className="detail-grid">
                      {[["bankName","Bank Name"],["accountNumber","Account Number"],["ifsc","IFSC Code"],["branch","Branch"]].map(([name, label]) => (
                        <div key={name} className="detail-item">
                          <label>{label}</label>
                          <input name={name} value={editForm[name] || ""} onChange={handleEditChange} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>Hierarchy IDs</h4>
                    <div className="detail-grid">
                      {[["districtId","District ID"],["blockId","Block ID"],["gpId","GP ID"]].map(([name, label]) => (
                        <div key={name} className="detail-item">
                          <label>{label}</label>
                          <input name={name} value={editForm[name] || ""} onChange={handleEditChange} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="detail-section">
                    <h4>Upload Images</h4>
                    <div className="images-grid">
                      {["aadhaar", "pan", "passbook", "photo"].map(type => (
                        <div key={type} className="upload-image-item">
                          <label>{type.charAt(0).toUpperCase() + type.slice(1)}</label>
                          <input type="file" accept="image/*"
                            onChange={e => handleEditFileChange(type, e.target.files[0])} />
                          {(editPreviews[type] || editForm[`${type}Base64`]) && (
                            <img src={editPreviews[type] || editForm[`${type}Base64`]} alt={type}
                              width="60" style={{ marginTop: "8px",
                                borderRadius: type === "photo" ? "50%" : "8px" }} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )
            ) : (
              <div className="no-data">Failed to load details.</div>
            )}
          </div>

          <div className="modal-footer">
            <button className="btn-secondary" onClick={closeModal}>Close</button>
            {/* Edit button — ADMIN only */}
            {isAdmin && !isEditing && (
              <button className="btn-primary" onClick={() => { if (isAdmin) setIsEditing(true); }}>✏️ Edit</button>
            )}
            {isAdmin && isEditing && (
              <>
                <button className="btn-secondary" onClick={() => {
                  setIsEditing(false);
                  setEditForm(memberDetails);
                  setEditPreviews({ aadhaar: null, pan: null, passbook: null, photo: null });
                }}>Cancel</button>
                <button className="btn-primary" onClick={saveMember} disabled={modalLoading}>💾 Save</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Lightbox
  const renderLightbox = () => lightboxImage && (
    <div className="lightbox-overlay" onClick={() => setLightboxImage(null)}>
      <div className="lightbox-content" onClick={e => e.stopPropagation()}>
        <img src={lightboxImage} alt="Full size" />
        <button className="lightbox-close" onClick={() => setLightboxImage(null)}>×</button>
      </div>
    </div>
  );

  // ==================== LOADING ====================
  if (loading || !currentUser) {
    return (
      <div style={{ display: "flex" }}>
        <Sidebar />
        <div style={{ padding: "40px" }}>Loading teams...</div>
      </div>
    );
  }

  // ==================== ADMIN / STATE COORDINATOR VIEW ====================
  if (currentUser.role === "STATE_COORDINATOR" || currentUser.role === "ADMIN") {
    const title    = currentUser.role === "ADMIN" ? "Teams Overview" : "State Coordinator Dashboard";
    const subtitle = currentUser.role === "ADMIN"
      ? "Hierarchy of coordinators – District → Block → GP"
      : "Viewing all districts across the state";
    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>{title}</h1>
            <p>{subtitle}</p>
           <FilterBar
  districts={districts}
  selectedDistrict={selectedDistrict}
  handleDistrictChange={handleDistrictChange}
  searchTerm={searchTerm}
  setSearchTerm={setSearchTerm}
/>
          </div>
          <div className="teams-container">
            {visibleDistricts.length === 0 && !selectedDistrict && (
              <div className="no-data">Select a district from the dropdown to view data.</div>
            )}
            {visibleDistricts.length === 0 && selectedDistrict && (
              <div className="no-data">No data found for selected district.</div>
            )}
            {visibleDistricts.map(district => (
              <DistrictSection key={district.districtId} district={district} />
            ))}
          </div>
        </div>
        {renderModal()}
        {renderLightbox()}
      </div>
    );
  }

  // ==================== DISTRICT COORDINATOR VIEW ====================
  if (currentUser.role === "DISTRICT_COORDINATOR") {
    const myDistrict = districts.find(d => d.districtId === currentUser.districtId);
    if (!myDistrict) return <div>District not found</div>;
    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>My District: {myDistrict.districtName}</h1>
            <div className="search-bar">
              <input type="text" placeholder="🔍 Search..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="teams-container">
            <DistrictSection district={myDistrict} />
          </div>
        </div>
        {renderModal()}
        {renderLightbox()}
      </div>
    );
  }

  // ==================== BLOCK COORDINATOR VIEW ====================
  if (currentUser.role === "BLOCK_COORDINATOR") {
    let blockName = "Unknown Block";
    for (const blocks of Object.values(blocksMap)) {
      const found = blocks.find(b => b.id === currentUser.blockId);
      if (found) { blockName = found.name; break; }
    }
    const bCoords = filteredBlockCoords.filter(c => c.blockId === currentUser.blockId);
    const gps     = gpsMap[currentUser.blockId] || [];
    const bKey    = `block-root-block-${currentUser.blockId}`;
    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>My Block: {blockName}</h1>
            <div className="search-bar">
              <input type="text" placeholder="🔍 Search..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="teams-container">
            <div className="team-block">
              <div className="team-block-header">
                <span className="block-title">📍 {blockName}</span>
                <span className="member-count">{bCoords.length} BC(s)</span>
              </div>
              <div className="team-gps">
                {bCoords.length > 0 && (
                  <div className="coordinator-list">
                    {bCoords.map(coord => (
                      <div key={coord.id} className="coordinator-chip" onClick={() => openMemberModal(coord)}>
                        👤 {coord.name} {getPaymentBadge(coord.id)}
                      </div>
                    ))}
                  </div>
                )}
                {gps.map(gp => <GpSection key={gp.id} gp={gp} bKey={bKey} />)}
              </div>
            </div>
          </div>
        </div>
        {renderModal()}
        {renderLightbox()}
      </div>
    );
  }

  // ==================== GP COORDINATOR VIEW ====================
  if (currentUser.role === "GP_COORDINATOR") {
    let gpName = "Unknown GP";
    for (const gps of Object.values(gpsMap)) {
      const found = gps.find(g => g.id === currentUser.gpId);
      if (found) { gpName = found.name; break; }
    }
    const gCoords = filteredGpCoords.filter(c => c.gpId === currentUser.gpId);
    return (
      <div className="teams-app">
        <Sidebar />
        <div className="teams-main">
          <div className="teams-header">
            <h1>My Gram Panchayat: {gpName}</h1>
            <div className="search-bar">
              <input type="text" placeholder="🔍 Search..." value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)} />
            </div>
          </div>
          <div className="teams-container">
            <div className="team-members">
              {gCoords.map(coord => (
                <div key={coord.id} className="member-card" onClick={() => openMemberModal(coord)}>
                  <div className="member-avatar">👤</div>
                  <div className="member-info">
                    <div className="member-name">{coord.name}</div>
                    <div className="member-role">{coord.role}</div>
                    <div className="member-email">{coord.email}</div>
                    {getPaymentBadge(coord.id)}
                  </div>
                </div>
              ))}
              {gCoords.length === 0 && <div className="no-members">No coordinators assigned</div>}
            </div>
          </div>
        </div>
        {renderModal()}
        {renderLightbox()}
      </div>
    );
  }

 
   return <div>Role not supported</div>;
}

// 👇 OUTSIDE Teams()
const FilterBar = ({
  districts,
  selectedDistrict,
  handleDistrictChange,
  searchTerm,
  setSearchTerm,
}) => {
  return (
    <div className="search-bar">
      <select
        value={selectedDistrict}
        onChange={handleDistrictChange}
      >
        <option value="">All Districts</option>

        {districts.map((d) => (
          <option
            key={d.districtId}
            value={d.districtId}
          >
            {d.districtName}
          </option>
        ))}
      </select>

      <input
        type="text"
        placeholder="🔍 Search by name or email..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
    </div>
  );
};

